<template>
        <router-link class='pool' :to="`/${pool.routeOverride || pool.id}`">
            <td class='pooltext'>
                <CryptoIconFactoPool
                    :pool='pool'
                    absolutelyPositioned
                />
                {{pool.name}}
                <br class="small-screen-break" />
                <span class="reference-asset" :data-coin-type='pool.referenceAsset'>{{pool.referenceAsset}}</span>
                <span class="innovation-zone-label" v-show="pool.isRiskier">Innovation Zone</span>

                <span v-show="pool.referenceAsset == 'crypto'" class="innovation-zone-label">V2 </span>
                <span v-show="pool.referenceAsset == 'crypto'" class='tooltip'>
                  <span style="margin-left:3px;">[?]</span>
                      <span class='tooltiptext'>
                        V2 pools contain non pegged assets. Liquidity providers in those pools are exposed to all assets in the pool.
                      </span>
                  </span>
                <span v-if="containsRen1Assets" class='tooltip renv1'>
                    <span style="margin-left:3px;">[!!]</span>
                    <span class='tooltiptext long'>
                        If you have any assets in this pool, it requires IMMEDIATE action! Please click on the pool to learn more.
                    </span>
                </span>
                <br/>
                <span v-if="!pool.useAssetsStringForDisplayPurposes" class="assets-list">
                    <span v-for="coin in pool.coins" class="pool-asset" >{{coin.symbol}}</span>
                </span>
                <span v-else class="assets-list">
                    <span class="pool-asset">{{pool.assets}}</span>
                </span>
            </td>
            <td>
                <div class='base-apr'>
                    <span class='tooltip'>
                        <span :class="{'loading line': dailyAPY === null}">{{BN(dailyAPY).toFixed(2)}}</span>%
                        <span class='tooltiptext long'>
                            <div>Pool APY<span v-show="pool.isLendingPool"> + Lending APY</span> (annualized)</div>
                            <div>Daily APY: {{BN(dailyAPY).toFixed(2)}}%</div>
                            <div>Weekly APY: {{BN(weeklyAPY).toFixed(2)}}%</div>
                            <div v-if="typeof monthlyAPY !== 'undefined'">Monthly APY: {{monthlyAPY}}%</div>
                            <div v-if="typeof totalAPY !== 'undefined'">Total APY: {{totalAPY}}%</div>
                        </span>
                    </span>
                </div>
                <div class='apr'>
                    <div v-show="!pool.hasNoCRVRewards && !pool.isPendingGaugeVoteToStartCrvRewards && !pool.hasNoGauge && (!crvAPYs || crvAPYs[0] === undefined)" class="incentive-apr">
                        <span class="loading line">+</span>%
                    </div>
                    <div v-if="!pool.isPendingGaugeVoteToStartCrvRewards && crvAPYs && typeof crvAPYs[0] !== 'undefined' && crvAPYs[0] !== null" :class="{'incentive-apr': true}">
                        +{{crvAPYs && crvAPYs[0] && crvAPYs[0].toFixed(2)}}%
                        <span v-if="areBoostsEnabled && crvAPYs && crvAPYs.length > 1">
                            &ZeroWidthSpace;<span class="nowrap"><span class="right-arrow" />{{crvAPYs && crvAPYs[1].toFixed(2)}}%&nbsp;<span class="mobile-show"> CRV</span></span>
                            <span class="tooltip mobile-hide"><span class="symbol">CRV</span>
                                <span class='tooltiptext long'>
                                    CRV LP reward annualized (max APY can be reached with max boost of {{crvAPYs &&  crvAPYs[2].toFixed(2)}})
                                </span>
                            </span>
                            <span v-if='crvAPYs && crvAPYs[3]'><br>my: {{crvAPYs && crvAPYs[3] > 0 && crvAPYs[3].toFixed(2)}}%<span class="mobile-hide">&nbsp;<span class="symbol">CRV</span></span></span>
                        </span>
                        <span v-else>
                            <span class='tooltip'><span class="symbol">CRV</span>
                                <span class='tooltiptext'>
                                    CRV LP reward annualized
                                </span>
                            </span>
                        </span>
                        <span v-if="pool.rewardsNeedNudging">&nbsp;
                            <span class='tooltip'><span class="symbol">⧗</span>
                                <span class='tooltiptext long'>
                                    This pool has CRV rewards that aren’t streaming yet.<br />
                                    Head to this pool’s “Withdraw/Claim” page and click on the “Nudge CRV rewards” button to resume reward streaming for you and everyone else!
                                </span>
                            </span>
                        </span>
                        <span v-if="pool.areCrvRewardsStuckInBridge">&nbsp;
                            <span class='tooltip'><span class="symbol">✕</span>
                                <span class='tooltiptext long'>
                                    This pool has CRV rewards that aren’t currently being distributed.<br />
                                    This pool has a very small amount of rewards waiting to be distributed to it; but since the amount of rewards is very small, the bridge used to send these CRV tokens over is holding onto those tokens until they grow to an amount big enough to be bridged. If this pool continues receiving votes, the amount of tokens to be distributed will grow every Thursday, and eventually unlock and be distributed then.
                                </span>
                            </span>
                        </span>
                    </div>
                    <div v-show="!pool.isPendingGaugeVoteToStartCrvRewards && !crvAPYs && !additionalRewards.length">
                      --
                    </div>
                    <div v-show="pool.isPendingGaugeVoteToStartCrvRewards">
                        &nbsp;<span class='tooltip'>CRV TBA
                            <span class='tooltiptext long'>
                                Gauge vote pending. Rewards expected {{pool.expectedCrvRewardsStart}}.
                            </span>
                        </span>
                    </div>
                    <div v-show="additionalRewards.length > 0">
                        <div v-for='reward in additionalRewards' class="incentive-apr">
                            <span :class="{'loading line': !reward.rewardsAmount && reward.rewardsAmount !== 0}">+{{reward.rewardsAmount | toFixed2}}</span>%
                            <span class='tooltip'><span class="symbol">{{reward.name}}</span>
                                <span class='tooltiptext'>
                                    {{reward.name}} LP reward annualized
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </td>
            <td class='volume'>
                <span v-if="pool.id !== 'atricrypto' && pool.id !== 'atricrypto2' && pool.id !== 'atricrypto3' && pool.id !== 'tricrypto'" :class="{'loading line': volumes && volumes[0] < 0}">
                    <span v-show='(volumes && volumes[0]) > 0'>
                        <span class='tooltip'>
                            ${{formatLargeNumber((volumes && volumes[0]) || 0)}}
                            <span class='tooltiptext'>
                                Daily volume: ${{((volumes && volumes[0]) || 0) | formatNumber(0)}}
                            </span>
                        </span>
                    </span>
                    <span v-show='!volumes || !volumes[0]'>$0</span>
                </span>
                <span v-if="pool.id === 'atricrypto' || pool.id === 'atricrypto2' || pool.id === 'atricrypto3' || pool.id === 'tricrypto'" :class="{'loading line': volumes && volumes[1] < 0}">
                    <span v-show='(volumes && volumes[1]) > 0'>
                        <span class='tooltip'>
                            ${{formatLargeNumber((volumes && volumes[1]) || 0)}}
                            <span class='tooltiptext'>
                                Daily volume: ${{((volumes && volumes[1]) || 0) | formatNumber(0)}}
                            </span>
                        </span>
                    </span>
                    <span v-show='!volumes || !volumes[1]'>$0</span>
                </span>
            </td>
            <td class='tvl' v-if="hasTvlDataAvailable">
                <span :class="{'loading line': typeof usdTotal === 'undefined'}">
                    <span v-if="typeof usdTotal !== 'undefined'">${{formatLargeNumber(usdTotal)}}</span>
                </span>
            </td>
            <td :class="{ balance: true, showPersonalStats }">
                <span class='mobile-show' v-show='balances > 0'>${{(balances && balances.toFixed(2)) | formatNumber(0)}} </span>
                <span class='mobile-hide' v-show='showPersonalStats && balances >= 0'>
                    ${{formatLargeNumber(balances || 0, 1)}}
                </span>
                <span class='tooltip mobile-hide' v-show='!showPersonalStats && balances > 0'>
                    <img :src="publicPath + 'dollar-sign-solid.svg'">
                    <span class='tooltiptext'>Balance: ${{(balances && balances.toFixed(2)) | formatNumber(0)}}</span>
                </span>
            </td>
            <td v-show="showPersonalStats">
                <span :class="{'loading line': dailyAPY === null}"><span v-if="usdPerDay >= 0">{{formatLargeNumber(usdPerDay)}}</span><span class="symbol"> USD/day</span></span>
                <span :class="{'loading line': dailyAPY === null}" v-if="!pool.isPendingGaugeVoteToStartCrvRewards && !pool.hasNoGauge && crvAPYs && crvAPYs[3]">
                    <br />
                    <span class="tooltip">
                        <span v-if="crvPerDay >= 0">{{formatLargeNumber(crvPerDay)}}</span>
                        <span class="symbol"> CRV/day</span>
                        <span class='tooltiptext'>
                            <span v-if="!pool.isPendingGaugeVoteToStartCrvRewards && !pool.hasNoGauge && crvAPYs && crvAPYs[3] && crvPerDay >= 0" class="symbol">≈${{formatLargeNumber(crvPerDay * CRVprice)}}/day</span>
                            <span v-else>=$0/day</span>
                        </span>
                    </span>
                </span>
                <span v-for='reward in additionalRewardsPerDay' class="incentive-apr">
                    <br />
                    <span class="tooltip">
                        <span>{{formatLargeNumber(reward.rewardsAmount)}}</span>
                        <span class="symbol"> {{reward.name}}/day</span>
                        <span class='tooltiptext'>
                            <span class="symbol">≈${{formatLargeNumber(reward.rewardsAmount * reward.price)}}/day</span>
                        </span>
                    </span>
                </span>
            </td>
            <td v-show="showPersonalStats">
                <span class="tooltip" v-if="!pool.hasNoGauge || pool.id !== 'eurs'">
                    <span :class="{'loading line': typeof claimableCRV !== 'number'}">
                        <span v-show="typeof claimableCRV === 'number'">{{formatLargeNumber(claimableCRV)}}</span>
                        <span class="symbol"> CRV</span>
                    </span>
                    <span class='tooltiptext'>
                        <span class="symbol">≈${{formatLargeNumber(claimableCRV * CRVprice)}}</span>
                    </span>
                </span>
                <span v-for='claimableReward in claimableAdditionalRewards' class="incentive-apr">
                    <br />
                    <span class="tooltip">
                        <span>{{formatLargeNumber(claimableReward.amount)}}</span>
                        <span class="symbol"> {{claimableReward.name}}</span>
                        <span class='tooltiptext'>
                            <span class="symbol">≈${{formatLargeNumber(claimableReward.amount * claimableReward.price)}}</span>
                        </span>
                    </span>
                </span>
            </td>
        </router-link>
</template>

<script>
    import BN from 'bignumber.js';
    import config from '@/constants/config';
    import coins from '@/constants/coins';
    import CryptoIconFactoPool from './CryptoIconFactoPool.vue';
    import * as helpers from '../../utils/helpers';
    import { formatLargeNumber } from '../../utils/Number';

	export default {
        components: {
			CryptoIconFactoPool,
		},

        props: {
            pool: {
                required: true,
                type: Object,
            },
            CRVprice: {
                type: Number,
            },
            dailyAPY: {
                type: Number,
            },
            weeklyAPY: {
                type: Number,
            },
            monthlyAPY: {
                type: Number,
            },
            totalAPY: {
                type: Number,
            },
            crvAPYs: {
                type: Array,
            },
            volumes: {
                type: Array,
            },
            usdTotal: {
                type: Number,
            },
            balances: {
                type: Number,
            },
            usdPerDay: {
                type: Number,
            },
            crvPerDay: {
                type: Number,
            },
            claimableCRV: {
                type: Number,
            },
            additionalRewards: {
                type: Array,
            },
            additionalRewardsPerDay: {
                type: Array,
            },
            claimableAdditionalRewards: {
                type: Array,
            },
            showPersonalStats: {
                type: Boolean,
                required: true,
            },
        },
        data: () => ({
            config,
            areBoostsEnabled: config.blockchainId === 'ethereum',
		}),
		computed: {
            publicPath: () => process.env.BASE_URL,
            hasTvlDataAvailable: () => !!config.mainPoolsEndpoint,
            containsRen1Assets() {
                const poolCoins = [
                    ...this.pool.coins,
                    ...(this.pool.metaCoins || []),
                ];
                return (
                    'renbtc' in coins &&
                    poolCoins.some((coin) => coin.id === coins.renbtc.id)
                );
            },
        },
		async created() {},
        methods: {
            BN,
            formatNumber(num) {
				return helpers.formatNumber(num)
			},
            formatLargeNumber,
        },
	}
</script>

<style scoped>
    .assets-list {
        font-weight: normal;
    }
    .pool-asset {
        position: relative;
        margin-right: 11px;
    }
    .pool-asset:not(:last-child)::after {
        content: "+";
        position: absolute;
        right: -10px;
    }
    /* .reference-asset[data-coin-type="eur"] {
        background-color: #999;
    }
    .reference-asset[data-coin-type="eth"] {
        background-color: #81a8f8;
    }
    .reference-asset[data-coin-type="btc"] {
        background-color: #e0a76d;
    }
    .reference-asset[data-coin-type="link"] {
        background-color: #8294cb;
    } */

    .pool {
        display: table-row;
    }
    .pool:hover {
        background-color: blue;
        color: white;
        cursor: pointer;
    }
    .pool > a:hover .tooltip {
 		border-bottom: 1px dotted white;
	}
	.pool > a:hover img {
		filter: invert(1);
	}
	.pool a span {
		text-align: left;
	}
    td {
        padding: 5px 0;
    }
    @media only screen and (max-device-width: 730px) {
        td {
            padding-left: 2px;
            padding-right: 2px;
        }
        table:not(.showPersonalStats) td:nth-child(even) {
            background: rgba(255, 255, 255, 0.1);
        }
        .showPersonalStats td:nth-child(2),
        .showPersonalStats td:nth-child(5),
        .showPersonalStats td:nth-child(7) {
            background: rgba(255, 255, 255, 0.1);
        }
    }
    @media only screen and (min-device-width: 900px) {
        .small-screen-break {
            display: none;
        }
    }
    .pooltext {
        max-width: 240px;
        padding-left: 32px;
        position: relative;
    }
    .reference-asset {
        padding: 0px 3px;
        background: #212121;
        border-radius: 2px;
        color: #fff;
        opacity: 0.6;
        font-size: 12px;
        text-transform: uppercase;
    }
    .innovation-zone-label {
        display: inline-block;
        margin-left: 5px;
        padding: 0 2px;
        background: transparent;
        border: 1px solid #212121;
        line-height: 1em;
        border-radius: 2px;
        opacity: 0.6;
        font-size: 12px;
        text-transform: uppercase;
    }
    .symbol {
        font-weight: normal;
    }
    .tooltip {
		position: relative;
		margin-left: 0;
	}
	.assets .tooltip {
		vertical-align: bottom;
		overflow: hidden;
	}
    .tooltiptext.long {
		max-width: 250px;
		position: absolute;
		margin-left: 0;
		transform: translateX(-50%) translateX(6px);
	}
	.tooltiptext.long a {
		display: inline-block;
		background: transparent;
		text-decoration: underline;
	}
	.tooltiptext.long > div {
		padding-left: 1em;
		text-align: left;
	}
	.tooltiptext.long > div:hover {
		background: none;
	}
    .tooltip.renv1 span:first-child {
        background-color: red;
        color: #fff;
    }
    .balance:not(.showPersonalStats) {
		width: 12px;
	}
    .balance img {
		height: 12px;
	}
    .poolsdialog > div {
		margin-top: 1em;
	}
    .mobile-show {
        display: none;
    }
    .nowrap {
        white-space: nowrap;
    }
    .right-arrow::before {
        content: "→";
        font-family: "sans-serif";
    }

    @media only screen and (max-device-width : 1000px) {
        .showPersonalStats .assets-list {
            display: inline-block;
            max-width: 100px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }

    @media only screen and (max-device-width : 730px) {
        .mobile-show {
            display: inline;
        }
        .mobile-hide {
            display: none;
        }
        .assets-list {
            display: inline-block;
            max-width: 100px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
        .tooltip {
            border-bottom: 0;
        }
        .tooltiptext {
            display: none;
        }

        .showPersonalStats .volume {
            display: none;
        }

        .showPersonalStats .tvl {
            background: none;
        }

        .balance.balance.balance {
            width: auto;
        }
    }
</style>
